// A draw.io diagram, read-only, wherever an author puts one — CCT-477.
//
// ⚠️ THE TOOLBAR IS THE VIEWER'S OWN. Zoom in and out, fit, layers, page navigation and panning
// all come from draw.io, measured on the live viewer. Rebuilding them would have produced a
// second, poorer toolbar competing with the real one, and it would have lost the thing that
// matters most: the diagram is rendered from its REAL XML, so library shapes and embedded images
// come out exactly as the author drew them.
//
// ⚠️ FULL SCREEN IS OURS, BECAUSE THE VIEWER HAS NONE. Measured: its toolbar offers zoom, fit,
// layers, print and export, and no full screen at all. In an app "full screen" means the app's
// own screen anyway, so a modal is the honest answer on both platforms.
import React, { useEffect, useState } from 'react';
import { Linking, Modal, Platform, Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import { useTheme } from '../context/ThemeContext';
import { getThemeColors } from '../utils/themeStyles';
import { useT } from '../i18n/useT';
import DiagramFrame from './DiagramFrame';
import { urlVisualiseur } from './diagramUrl';

/**
 * Width-to-height ratio the frame aims for — CCT-477.
 *
 * ⚠️ THE VIEWER RENDERS AT 100%, ALWAYS. Measured in a 1200x520 viewport, with and without
 * `fit=1`: the applied scale was 1 in every case. It does not shrink a large drawing and it does
 * not enlarge a small one. An earlier comment here claimed it "fits the page"; that was wrong,
 * and it sent two fixes in the wrong direction -- resizing the frame could never change what the
 * reader sees.
 *
 * So the FILE's own size is the only lever, and the frame's job is simply to match its shape.
 * The four chapter-1 diagrams are authored at ~1700 px wide with 21 px text, and measure
 * 1699x809, 1699x779, 1718x779 and 1699x810 -- that is 2.10 to 2.21.
 */
const RATIO = 2.1;

/**
 * ⚠️ HIGH ENOUGH TO SHOW A WHOLE DIAGRAM, since nothing scales it down. At 100% a 1699x809
 * drawing needs 809 px of height; a cap of 520 cut it in half and no scrolling would reveal the
 * rest without panning. Beyond this the frame would start eating the page instead of serving it.
 */
const HAUTEUR_MAX = 860;

type Props = {
  /** The author's diagram address. Anything unusable renders nothing at all. */
  url?: string | null;
  /** MINIMUM height in the flow of the element; the frame grows with its width. Full screen
   *  ignores it. */
  hauteur?: number;
  /** Optional caption shown under the frame. */
  legende?: string | null;
  /**
   * The author's zoom, in percent (CCT-477). Absent = 100.
   *
   * ⚠️ IT EXISTS BECAUSE THE VIEWER HAS NO ZOOM OF ITS OWN. Measured with and without `fit=1`:
   * it always renders at scale 1. Only the author knows how dense their drawing is, so only
   * they can say how large it must read.
   */
  zoom?: number | null;
};

export default function DiagramViewer({ url, hauteur = 260, legende, zoom }: Props) {
  const src = urlVisualiseur(url);
  const [plein, setPlein] = useState(false);
  /**
   * The viewer could not be displayed HERE (CCT-477).
   *
   * ⚠️ MEASURED, AND NOT OUR DOING. In Edge the frame showed "viewer.diagrams.net a refusé de se
   * connecter" while the server answered 200 with no `X-Frame-Options` and no `frame-ancestors`:
   * the block came from the reader's machine. We cannot prevent it; we can refuse to leave an
   * empty rectangle, which reads as a broken screen rather than as a blocked one.
   */
  const [bloque, setBloque] = useState(false);
  useEffect(() => { setBloque(false); }, [url]);
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  const t = useT();
  const { height: hEcran, width: lEcran } = useWindowDimensions();
  /**
   * ⚠️ MEASURED WIDTH, NOT THE WINDOW'S. The frame sits inside a card, a chat bubble or a scene
   * column, each with its own padding: deriving the height from `useWindowDimensions` would
   * overshoot in every one of them. `onLayout` reports the width actually granted.
   */
  const [largeur, setLargeur] = useState(0);
  const hCadre = largeur > 0
    ? Math.round(Math.min(HAUTEUR_MAX, Math.max(hauteur, largeur / RATIO)))
    : hauteur;

  // ⚠️ NOTHING RATHER THAN AN EMPTY BOX. An element with no diagram, or with an address we refuse
  // (not https, hence blocked as mixed content), must not leave a blank white rectangle that the
  // reader takes for a broken screen.
  if (!src) return null;

  const facteur = Math.max(0.25, Math.min(4, (Number(zoom) || 100) / 100));
  const cadre = (h: number, w = largeur) => (
    <DiagramFrame src={src} largeur={w} hauteur={h} zoom={facteur} onEchec={() => setBloque(true)} />
  );

  return (
    <View style={[styles.boite, { borderColor: colors.border, backgroundColor: colors.cardBackground }]}
          onLayout={(e) => setLargeur(e.nativeEvent.layout.width)}>
      {bloque ? (
        <View style={[styles.repli, { height: hCadre }]}>
          <Ionicons name="git-network-outline" size={28} color={colors.textTertiary} />
          <Text style={[styles.repliTexte, { color: colors.textSecondary }]}>
            {t('diagram.blocked')}
          </Text>
          <Pressable onPress={() => { void Linking.openURL(src); }}
                     style={[styles.repliBouton, { borderColor: colors.border }]}
                     accessibilityRole="link">
            <Text style={[styles.repliLien, { color: colors.primary }]}>{t('diagram.open')}</Text>
          </Pressable>
        </View>
      ) : cadre(hCadre)}

      <Pressable
        onPress={() => setPlein(true)}
        style={[styles.plein, { backgroundColor: colors.cardBackground, borderColor: colors.border }]}
        accessibilityRole="button"
        accessibilityLabel={t('diagram.fullscreen')}
        hitSlop={8}
      >
        <Ionicons name="expand-outline" size={18} color={colors.textPrimary} />
      </Pressable>

      {legende ? (
        <Text style={[styles.legende, { color: colors.textTertiary }]}>{legende}</Text>
      ) : null}

      {/* ⚠️ NO FULL-SCREEN BUTTON WHEN THE FRAME IS BLOCKED: enlarging nothing is still nothing,
          and offering the gesture would promise a rendering we cannot deliver. */}
      <Modal visible={plein && !bloque} animationType="slide" onRequestClose={() => setPlein(false)}
             supportedOrientations={['portrait', 'landscape']}>
        <View style={{ flex: 1, backgroundColor: colors.background }}>
          <View style={[styles.barre, { borderColor: colors.border }]}>
            <Text style={[styles.titre, { color: colors.textPrimary }]} numberOfLines={1}>
              {legende || t('diagram.title')}
            </Text>
            <Pressable onPress={() => setPlein(false)} hitSlop={10}
                       accessibilityRole="button" accessibilityLabel={t('common.close')}>
              <Ionicons name="close" size={24} color={colors.textPrimary} />
            </Pressable>
          </View>
          {/* ⚠️ A FRESH FRAME, NOT THE SAME ONE MOVED. Re-parenting a WebView across a Modal
              reloads it on Android anyway, and on web it would lose the iframe's zoom state;
              mounting a second one keeps both predictable. */}
          {cadre(Math.max(240, hEcran - (Platform.OS === 'web' ? 120 : 160)), lEcran)}
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  boite: { borderWidth: 1, borderRadius: 12, overflow: 'hidden', marginVertical: 10 },
  plein: {
    position: 'absolute', top: 8, right: 8, width: 34, height: 34, borderRadius: 17,
    borderWidth: 1, alignItems: 'center', justifyContent: 'center', opacity: 0.92,
  },
  legende: { fontSize: 12, paddingHorizontal: 12, paddingVertical: 8 },
  repli: { alignItems: 'center', justifyContent: 'center', gap: 10, paddingHorizontal: 20 },
  repliTexte: { fontSize: 13, textAlign: 'center', lineHeight: 18 },
  repliBouton: { borderWidth: 1, borderRadius: 8, paddingHorizontal: 14, paddingVertical: 8 },
  repliLien: { fontSize: 13, fontWeight: '700' },
  barre: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1,
  },
  titre: { fontSize: 16, fontWeight: '700', flex: 1, marginRight: 12 },
});
